import { ContentHeader } from "../components/ContentHeader";
import { useApi } from "../hooks/useApi";

// ---- Response shapes (local — not shared via api/types.ts) ----
interface MobileSection {
  status?: string;
  crash_free_rate: number | null;
  ios_crash_free: number | null;
  android_crash_free: number | null;
}
interface PingRow {
  url: string;
  status: number | null;
  ok: boolean;
  error?: string;
}
interface UptimeSection {
  ok: number;
  total: number;
  rows: PingRow[];
}
interface ExperienceResponse {
  status: "ok";
  mobile: MobileSection | null;
  uptime: UptimeSection | null;
}

const TITLE = "User Experience";
const DESC = "Mobile crash-free rate (Crashlytics) and surface/tool reachability (synthetic pings).";

// A crash-free value may arrive as a fraction (0.992) or a percentage (99.2).
function toPct(v: number | null): number | null {
  if (v == null) return null;
  return v <= 1 ? v * 100 : v;
}
function fmtPct(v: number | null): string {
  const p = toPct(v);
  return p == null ? "—" : `${p.toFixed(2)}%`;
}
// ok if ≥99%, else warn (only when we have a value).
function pctCls(v: number | null): string {
  const p = toPct(v);
  if (p == null) return "muted";
  return p >= 99 ? "ok" : "warn";
}

export function ExperienceTab() {
  const { data, loading, error } = useApi<ExperienceResponse>("/command-center/experience");

  if (loading) {
    return (
      <>
        <ContentHeader title={TITLE} description={DESC} />
        <div className="loading"><div className="loading-spinner" /><p>Loading experience signals…</p></div>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        <ContentHeader title={TITLE} description={DESC} />
        <div className="empty">Could not load experience signals. Is command-center-api running?</div>
      </>
    );
  }

  const { mobile, uptime } = data;

  return (
    <>
      <ContentHeader title={TITLE} description={DESC} />

      {/* ---- Mobile crash-free ---- */}
      <div className="section-header"><h2>Mobile crash-free</h2></div>
      {mobile == null ? (
        <div className="empty" style={{ marginBottom: 18, padding: "14px 18px", textAlign: "start" }}>
          Mobile crash-free is unavailable — set <code>CRASHLYTICS_BQ_PROJECT</code> and{" "}
          <code>GCP_SERVICE_ACCOUNT_JSON</code> on the API to wire the Crashlytics → BigQuery path.
        </div>
      ) : (
        <div className="cov-summary">
          <span className={`cov-pill ${pctCls(mobile.crash_free_rate)}`}>
            <span className="dot" /><strong>{fmtPct(mobile.crash_free_rate)}</strong> overall
          </span>
          {mobile.ios_crash_free != null && (
            <span className={`cov-pill ${pctCls(mobile.ios_crash_free)}`}>
              <span className="dot" /><strong>{fmtPct(mobile.ios_crash_free)}</strong> iOS
            </span>
          )}
          {mobile.android_crash_free != null && (
            <span className={`cov-pill ${pctCls(mobile.android_crash_free)}`}>
              <span className="dot" /><strong>{fmtPct(mobile.android_crash_free)}</strong> Android
            </span>
          )}
        </div>
      )}

      {/* ---- Surface / tool uptime ---- */}
      <div className="section-header">
        <h2>Surface uptime</h2>
        {uptime && <span className="count">{uptime.total}</span>}
      </div>
      {uptime == null ? (
        <div className="empty" style={{ marginBottom: 18, padding: "14px 18px", textAlign: "start" }}>
          No reachability targets configured — set <code>COCKPIT_PING_URLS</code> on the API.
        </div>
      ) : (
        <>
          <div className="cov-summary">
            <span className={`cov-pill ${uptime.ok < uptime.total ? "crit" : "ok"}`}>
              <span className="dot" /><strong>{uptime.ok}/{uptime.total}</strong> surfaces up
            </span>
          </div>
          {uptime.rows.length === 0 ? (
            <div className="empty">No reachability rows.</div>
          ) : (
            <table className="cov-matrix">
              <thead>
                <tr><th>URL</th><th>Status</th><th>Reachable</th></tr>
              </thead>
              <tbody>
                {uptime.rows.map((r) => (
                  <tr key={r.url}>
                    <td><code style={{ fontFamily: "var(--font-mono)" }}>{r.url}</code></td>
                    <td><span className="num">{r.status ?? "—"}</span></td>
                    <td className={`cov-cell ${r.ok ? "yes" : "no"}`} style={{ color: r.ok ? "var(--green)" : "var(--red)" }}>
                      {r.ok ? "up" : "down"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* ---- Web RUM hint ---- */}
      <div style={{ marginTop: 18, fontSize: 11.5, color: "var(--text-muted)" }}>
        Web RUM (Core Web Vitals) lights up when a Sentry/RUM beacon is connected.
      </div>
    </>
  );
}

export default ExperienceTab;
